import { hasMeaningfulOnboardingValue } from '@/lib/ai/onboardingFieldValues'

import { isLikelyLegalPageUrl } from './onboardingLegalUrls'

export type OnboardingFormData = {
  name: string
  website: string
  street: string
  city: string
  zipcode: string
  country: string
  state: string
  brokerageName: string
  licenseNumber: string
  /** US: full license title, e.g. "Real Estate Sales Associate". */
  licenseStatus: string
  /** US: "I work independently" — brokerage name mirrors `name` while ticked. */
  brokerageNameSameAsName: boolean
  ambassadorSalutation: string
  ambassadorFirstName: string
  ambassadorLastName: string
  phone: string
  ambassadorMobile: string
  ambassadorRole: string
  ambassadorEmail: string
  contactFirstName: string
  contactLastName: string
  contactPhone: string
  contactEmail: string
  commission: string
  slogan: string
  businessDescription: string
  googleReviewLink: string
  primaryColor: string
  secondaryColor: string
  linkImprint: string
  linkPrivacy: string
  linkAbout: string
  automaticIntegration: boolean
  companyLogo: string
  teamPicture: string
  portraitImage: string
}

export type ChatBillingPrefill = {
  billingFirstname?: unknown
  billingLastname?: unknown
  billingEmail?: unknown
  billingPhone?: unknown
  billingCompanyName?: unknown
  vatId?: unknown
  taxNumber?: unknown
  billingNoVat?: unknown
}

export type QuickscrapePayload = {
  hasCrawledSuccessfully: boolean
  hasExtractedInformation: boolean
  companyName: unknown
  companyStreetName: unknown
  companyStreetNumber: unknown
  companyZipCode: unknown
  companyCityName: unknown
  companyCountryName: unknown
  countryCode: unknown
  languageCode: unknown
  localeCode: unknown
  businessDescription: unknown
  slogan: unknown
}

export function createEmptyOnboardingFormData(): OnboardingFormData {
  return {
    name: '',
    website: '',
    street: '',
    city: '',
    zipcode: '',
    country: '',
    state: '',
    brokerageName: '',
    licenseNumber: '',
    licenseStatus: '',
    brokerageNameSameAsName: false,
    ambassadorSalutation: '',
    ambassadorFirstName: '',
    ambassadorLastName: '',
    phone: '',
    ambassadorMobile: '',
    ambassadorRole: '',
    ambassadorEmail: '',
    contactFirstName: '',
    contactLastName: '',
    contactPhone: '',
    contactEmail: '',
    commission: '',
    slogan: '',
    businessDescription: '',
    googleReviewLink: '',
    primaryColor: '',
    secondaryColor: '',
    linkImprint: '',
    linkPrivacy: '',
    linkAbout: '',
    automaticIntegration: false,
    companyLogo: '',
    teamPicture: '',
    portraitImage: '',
  }
}

export function chatBillingPrefillFromMaklerPrefill(
  prefill: ChatBillingPrefill | null | undefined
): ChatBillingPrefill | null {
  if (!prefill) return null
  return {
    billingFirstname: prefill.billingFirstname,
    billingLastname: prefill.billingLastname,
    billingEmail: prefill.billingEmail,
    billingPhone: prefill.billingPhone,
    billingCompanyName: prefill.billingCompanyName,
    vatId: prefill.vatId,
    taxNumber: prefill.taxNumber,
    billingNoVat: prefill.billingNoVat,
  }
}

/** Normalizes quickscrape JSON (camelCase, snake_case, stringified, or `{ data }` wrapper). */
export function normalizeQuickscrapePayload(raw: unknown): QuickscrapePayload | null {
  if (raw == null) return null
  let o: Record<string, unknown> | unknown = raw
  if (typeof raw === 'string') {
    try {
      o = JSON.parse(raw)
    } catch (e) {
      console.warn('[Onboarding] quickscrape: could not parse JSON string', e)
      return null
    }
  }
  if (typeof o !== 'object' || o == null) return null
  const rec = o as Record<string, unknown>
  if (
    rec.data &&
    typeof rec.data === 'object' &&
    ('hasExtractedInformation' in (rec.data as object) ||
      'has_extracted_information' in (rec.data as object) ||
      'companyName' in (rec.data as object) ||
      'company_name' in (rec.data as object))
  ) {
    o = rec.data
  }
  const src = o as Record<string, unknown>
  const p = (camel: string, snake: string) =>
    src[camel] !== undefined && src[camel] !== null ? src[camel] : src[snake]

  return {
    hasCrawledSuccessfully: Boolean(p('hasCrawledSuccessfully', 'has_crawled_successfully')),
    hasExtractedInformation: Boolean(p('hasExtractedInformation', 'has_extracted_information')),
    companyName: p('companyName', 'company_name'),
    companyStreetName: p('companyStreetName', 'company_street_name'),
    companyStreetNumber: p('companyStreetNumber', 'company_street_number'),
    companyZipCode: p('companyZipCode', 'company_zip_code'),
    companyCityName: p('companyCityName', 'company_city_name'),
    companyCountryName: p('companyCountryName', 'company_country_name'),
    countryCode: p('countryCode', 'country_code'),
    languageCode: p('languageCode', 'language_code'),
    localeCode: p('localeCode', 'locale_code'),
    businessDescription: p('businessDescription', 'business_description'),
    slogan: p('slogan', 'slogan'),
  }
}

/**
 * Fields bottimmo backfills into `makler_form_prefill` from the saved company
 * record (`_buildMaklerFormPrefillFromSession`). A prefill value equal to the
 * saved one is the user's own input coming back after a reload, not a capture.
 */
export const COMPANY_RECORD_BACKFILL_FIELDS = ['state', 'brokerageName', 'licenseNumber'] as const

type CompanyRecordValues = Partial<Record<(typeof COMPANY_RECORD_BACKFILL_FIELDS)[number], unknown>>

export function companyRecordValues(
  company:
    | { address?: { state?: unknown } | null; brokerageName?: unknown; licenseNumber?: unknown }
    | null
    | undefined
): CompanyRecordValues {
  return {
    state: company?.address?.state,
    brokerageName: company?.brokerageName,
    licenseNumber: company?.licenseNumber,
  }
}

/** True when `value` is the saved company-record value of a backfilled field. */
export function matchesSavedCompanyValue(
  key: string,
  value: unknown,
  savedValues: CompanyRecordValues
): boolean {
  if (!(COMPANY_RECORD_BACKFILL_FIELDS as readonly string[]).includes(key)) return false
  const saved = savedValues[key as keyof CompanyRecordValues]
  if (saved == null || value == null) return false
  const s = String(saved).trim()
  return s !== '' && s === String(value).trim()
}

function setPrefillField(
  formData: OnboardingFormData,
  userEditedFields: Set<string>,
  aiCapturedFields: Set<string>,
  key: keyof OnboardingFormData,
  val: unknown,
  savedValues: CompanyRecordValues = {}
): void {
  if (val == null || val === '') return
  const s = String(val).trim()
  if (!s) return
  if (!hasMeaningfulOnboardingValue(String(key), s)) return
  if (userEditedFields.has(String(key))) {
    const current = (formData as Record<string, unknown>)[String(key)]
    if (hasMeaningfulOnboardingValue(String(key), current)) return
  }
  ;(formData as Record<string, unknown>)[String(key)] = s
  if (matchesSavedCompanyValue(String(key), s, savedValues) && !aiCapturedFields.has(String(key)))
    return
  aiCapturedFields.add(String(key))
}

export function applyPrefillToForm(
  formData: OnboardingFormData,
  prefill: Record<string, unknown>,
  userEditedFields: Set<string>,
  aiCapturedFields: Set<string>,
  countryDisplayFromCode: (code: unknown) => string,
  savedValues: CompanyRecordValues = {}
): void {
  const setIf = (key: keyof OnboardingFormData, val: unknown) =>
    setPrefillField(formData, userEditedFields, aiCapturedFields, key, val, savedValues)

  setIf('name', prefill.name)
  setIf('street', prefill.street)
  setIf('zipcode', prefill.zipcode)
  setIf('city', prefill.city)
  setIf('state', prefill.state)
  setIf('brokerageName', prefill.brokerageName)
  setIf('licenseNumber', prefill.licenseNumber)
  if (prefill.linkAbout) {
    if (isLikelyLegalPageUrl(prefill.linkAbout)) setIf('linkAbout', prefill.linkAbout)
    else setIf('businessDescription', prefill.linkAbout)
  }
  if (prefill.linkImprint && isLikelyLegalPageUrl(prefill.linkImprint)) {
    setIf('linkImprint', prefill.linkImprint)
  }
  if (prefill.linkPrivacy && isLikelyLegalPageUrl(prefill.linkPrivacy)) {
    setIf('linkPrivacy', prefill.linkPrivacy)
  }
  setIf('businessDescription', prefill.businessDescription)
  setIf('googleReviewLink', prefill.googleReviewLink)
  setIf('slogan', prefill.slogan)
  setIf('primaryColor', prefill.primaryColor)
  setIf('secondaryColor', prefill.secondaryColor)
  setIf('ambassadorFirstName', prefill.ambassadorFirstName)
  setIf('ambassadorLastName', prefill.ambassadorLastName)
  setIf('ambassadorRole', prefill.ambassadorRole)
  setIf('phone', prefill.phone)
  setIf('ambassadorMobile', prefill.ambassadorMobile)
  setIf('ambassadorEmail', prefill.ambassadorEmail)
  setIf('contactFirstName', prefill.contactFirstName)
  setIf('contactLastName', prefill.contactLastName)
  setIf('contactPhone', prefill.contactPhone)
  setIf('contactEmail', prefill.contactEmail)
  setIf('commission', prefill.commission)
  if (['Herr', 'Frau'].includes(String(prefill.ambassadorSalutation || '').trim())) {
    setIf('ambassadorSalutation', prefill.ambassadorSalutation)
  }

  if (prefill.country && !userEditedFields.has('country')) {
    const display = countryDisplayFromCode(prefill.country)
    if (display) {
      formData.country = display
      aiCapturedFields.add('country')
    }
  }

  if (
    prefill.automaticIntegration != null &&
    !userEditedFields.has('automaticIntegration') &&
    !aiCapturedFields.has('automaticIntegration')
  ) {
    formData.automaticIntegration =
      prefill.automaticIntegration === true ||
      String(prefill.automaticIntegration).trim().toLowerCase() === 'true'
  }
}

export function applyQuickscrapeToForm(
  formData: OnboardingFormData,
  scrape: QuickscrapePayload,
  userEditedFields: Set<string>,
  aiCapturedFields: Set<string>,
  countryDisplayFromScrape: (scrape: QuickscrapePayload) => string
): void {
  const setIf = (key: keyof OnboardingFormData, val: unknown) =>
    setPrefillField(formData, userEditedFields, aiCapturedFields, key, val)

  const streetParts = [scrape.companyStreetName, scrape.companyStreetNumber]
    .filter(Boolean)
    .map((x) => String(x).trim())
    .filter(Boolean)
  if (streetParts.length && !userEditedFields.has('street')) {
    formData.street = streetParts.join(' ')
    aiCapturedFields.add('street')
  }

  setIf('name', scrape.companyName)
  setIf('zipcode', scrape.companyZipCode)
  setIf('city', scrape.companyCityName)
  const countryDisplay = countryDisplayFromScrape(scrape)
  if (countryDisplay && !userEditedFields.has('country')) {
    formData.country = countryDisplay
    aiCapturedFields.add('country')
  }
  setIf('businessDescription', scrape.businessDescription)
  setIf('slogan', scrape.slogan)
}
